import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { cssVariables } from "@/utils/utils";
import { Col, Descriptions, Popover, Row } from "antd";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import { LinkTargetType } from "../../../../common/types";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { AGENT_ROUTE_PATHS } from "../../paths";
import { AgentBase } from "../../types";
import { formatAgentAggregatedName } from "../../utils";
import { AgentTypeTag } from "./AgentTypeTag";

interface Props<T extends AgentBase & { representatives?: AgentBase[] }> {
  agent?: T;
  hideIdNumber?: boolean;
  showRepresentatives?: boolean;
  ellipsis?: boolean;
  showLink?: boolean;
  linkTarget?: LinkTargetType;
  textStyle?: React.CSSProperties;
  showStatuses?: boolean;
}

export const AgentTypeTagWithName = <T extends AgentBase & { representatives?: AgentBase[] }>({
  agent,
  hideIdNumber,
  showRepresentatives,
  ellipsis,
  showLink,
  linkTarget,
  textStyle,
  showStatuses,
}: Props<T>) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const representatives =
    agent?.representatives?.length && showRepresentatives
      ? `| ${agent.representatives?.map((representative) => representative.displayName).join(", ")}`
      : "";

  return agent ? (
    <Row gutter={0}>
      <Col>
        <AgentTypeTag type={agent.type} />
      </Col>

      <Col flex="1 1 0" style={{ minWidth: 0 }}>
        <Ellipsis
          tooltip={
            hideIdNumber
              ? `${agent.aggregatedName} ${representatives}`
              : `${formatAgentAggregatedName(agent)} ${representatives}`
          }
          shouldRender={!!ellipsis}
          style={textStyle}
        >
          {showStatuses ? (
            <Popover
              content={
                <Descriptions
                  size="small"
                  bordered
                  column={1}
                  items={[
                    {
                      key: "status",
                      label: isTopAgentPlus
                        ? t("agent.helpers.agentStatusSFAPlusLabel")
                        : t("agent.enums.agentStatus._label"),
                      children: agent.status ? t(`agent.enums.agentStatus.${agent.status}`) : "",
                    },
                    isTopAgentPlus
                      ? {
                          key: "secondaryStatus",
                          label: t("agent.helpers.agentSecondaryStatusSFAPlusLabel"),
                          children: agent.secondaryStatus
                            ? t(`agent.enums.agentSecondaryStatus.${agent.secondaryStatus}`)
                            : undefined,
                        }
                      : undefined,
                  ].filter((item) => !!item)}
                />
              }
            >
              <span>
                <AntIcon className="margin-right-tiny" style={{ cursor: "pointer" }} type="audit" />
              </span>
            </Popover>
          ) : undefined}

          {showLink ? (
            <Link
              className="no-link-color"
              style={textStyle}
              to={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: agent.id })}
              target={linkTarget}
            >
              {hideIdNumber ? agent.aggregatedName : formatAgentAggregatedName(agent)}
            </Link>
          ) : hideIdNumber ? (
            agent.aggregatedName
          ) : (
            formatAgentAggregatedName(agent)
          )}

          <i className="margin-left-tiny" style={{ color: cssVariables.colorGrey }}>
            {representatives}
          </i>
        </Ellipsis>
      </Col>
    </Row>
  ) : null;
};
